import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { CACHE_KEYS, Investment, useEditInvestment } from "@rentablo/api/apiv1";
import { SymbolSearch } from "@features/searchSymbol/components/SymbolSearch";
import { LoadingButton } from "@rentablo/components";

const Container = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};

  ${({ theme }) => theme.breakpoints.up("sm")} {
    width: 50%;
  }
`;

const SelectedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  margin: ${({ theme }) => theme.spacing(3, 0)};

  ${({ theme }) => theme.breakpoints.up("sm")} {
    flex-direction: row;
  }
`;

export interface Props {
  investment: Investment;
}

export const InvestmentEditQuoteData = ({ investment }: Props) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [symbol, setSymbol] = useState<SymbolWithQuote>();
  const { mutateAsync: update, isPending } = useEditInvestment();

  const handleSaveClick = () => {
    update(
      {
        id: investment.id,
        createOrUpdateInvestmentParams: {
          tickerSymbol: symbol?.quote?.id,
          quoteProvider: symbol?.quoteProvider,
        },
      },
      {
        onSuccess: async () => {
          enqueueSnackbar(
            `${t("investmentEditPage.basicTab.saved")}: ${investment.name}`,
            { autoHideDuration: 3000, variant: "success" }
          );
          await queryClient.invalidateQueries({
            queryKey: [CACHE_KEYS.INVESTMENTS_ID],
          });
        },
      }
    );
  };

  const selectedSymbolText = symbol?.name
    ? `${t("investmentEditPage.quoteDataTab.selectedText")} ${symbol?.name} ${symbol?.quote?.stockExchangeName ? `(${symbol?.quote?.stockExchangeName})` : ""}`
    : t("investmentEditPage.quoteDataTab.nothingSelectedText");

  return (
    <Container>
      <SelectedContainer>
        <Typography variant="body1">{selectedSymbolText}</Typography>
        <LoadingButton
          variant="primary"
          isFetching={isPending}
          disabled={isPending || !symbol}
          onClick={handleSaveClick}
          sx={{
            flexShrink: 0,
          }}
        >
          {t("investmentEditPage.quoteDataTab.saveButton")}
        </LoadingButton>
      </SelectedContainer>

      <SymbolSearch
        search={symbol?.name ?? investment.isin ?? investment.name}
        hasQuote={Boolean(symbol?.quote)}
        onSelectSymbol={(value) => setSymbol(value)}
        noManual
      />
    </Container>
  );
};
